import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  Award,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Clock,
  GraduationCap,
  Layers,
  Users,
} from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'
import { categoryMeta, type Course, type Enrollment } from './Learning'

interface CourseDetail extends Course {
  seatsRemaining: number | null
  myEnrollment: Enrollment | null
  quiz: { id: string; title: string; passScore: number; questionCount: number } | null
  myQuizBest: { scorePercent: number; passed: boolean; attemptCount: number } | null
  content: { moduleCount: number; lessonCount: number; totalMinutes: number } | null
}

const progressSteps = [0, 25, 50, 75, 100]

function formatLabel(format: string) {
  return format.charAt(0).toUpperCase() + format.slice(1)
}

export default function CourseDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')

  const [course, setCourse] = useState<CourseDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [acting, setActing] = useState(false)

  const load = useCallback(async () => {
    try {
      setCourse(await apiJson<CourseDetail>(`/courses/${id}`))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load course')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  const act = async (fn: () => Promise<unknown>) => {
    setActing(true)
    setError('')
    try {
      await fn()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed')
    } finally {
      setActing(false)
    }
  }

  const enroll = () => act(() => apiJson(`/courses/${id}/enroll`, { method: 'POST' }))
  const setProgress = (progress: number) =>
    act(() =>
      apiJson(`/enrollments/${course?.myEnrollment?.id}/progress`, {
        method: 'PATCH',
        body: JSON.stringify({ progress }),
        headers: { 'Content-Type': 'application/json' },
      }),
    )
  const drop = () => act(() => apiJson(`/enrollments/${course?.myEnrollment?.id}/drop`, { method: 'POST' }))

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  if (!course) {
    return (
      <div className="space-y-4">
        <Link to="/learning" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
          <ArrowLeft size={15} /> Back to Learning
        </Link>
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error || 'Course not found'}
        </div>
      </div>
    )
  }

  const cat = categoryMeta[course.category] ?? categoryMeta.technical
  const mine = course.myEnrollment
  const full = course.seatsRemaining !== null && course.seatsRemaining <= 0 && !mine
  const canEnroll = course.status === 'published' && (!mine || mine.status === 'dropped')

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center justify-between">
        <Link to="/learning" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
          <ArrowLeft size={15} /> Back to Learning
        </Link>
        {allowManage && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/courses/${course.id}/content/edit`)}
              className="rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              {course.content ? 'Edit content' : 'Add content'}
            </button>
            <button
              onClick={() => navigate(`/courses/${course.id}/quiz/edit`)}
              className="rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              {course.quiz ? 'Edit quiz' : 'Add quiz'}
            </button>
            <button
              onClick={() => navigate(`/courses/${course.id}/sessions/new`)}
              className="rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Schedule session
            </button>
            <button
              onClick={() => navigate(`/courses/${course.id}/assign`)}
              className="rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Assign
            </button>
            <button
              onClick={() => navigate(`/courses/${course.id}/edit`)}
              className="rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Edit course
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* header */}
        <div className="border-b border-slate-100 bg-gradient-to-r from-brand-600 to-brand-800 px-6 py-6 text-white">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-white/25">{cat.label}</span>
            <span className="inline-flex rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-white/25">{formatLabel(course.format)}</span>
            {course.certValidityMonths && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-white/25">
                <Award size={11} /> Certificate · {course.certValidityMonths} months
              </span>
            )}
            {course.quiz && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-white/25">
                <ClipboardList size={11} /> Quiz · pass {course.quiz.passScore}%
              </span>
            )}
            {course.content && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-white/25">
                <Layers size={11} /> {course.content.lessonCount} lessons
              </span>
            )}
            {course.status !== 'published' && (
              <span className="inline-flex rounded-full bg-amber-400/90 px-2.5 py-0.5 text-[11px] font-bold text-amber-950">
                {course.status.toUpperCase()}
              </span>
            )}
          </div>
          <h1 className="mt-2.5 text-xl font-bold tracking-tight">{course.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/75">
            {course.durationHours ? (
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {course.durationHours} hours</span>
            ) : null}
            <span className="inline-flex items-center gap-1">
              <Users size={12} /> {course.enrolledCount} enrolled
              {course.seatsRemaining !== null && ` · ${course.seatsRemaining} seats left`}
            </span>
            {course.instructorName && <span className="inline-flex items-center gap-1"><GraduationCap size={12} /> {course.instructorName}</span>}
            <span>v{course.version}</span>
          </div>
        </div>

        <div className="space-y-6 px-6 py-6">
          {/* description */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">About this course</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">
              {course.description || 'No description provided.'}
            </p>
          </div>

          {/* enrollment state */}
          {mine && mine.status !== 'dropped' ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Your progress</h2>
                {mine.status === 'completed' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                    <CheckCircle2 size={11} /> Completed {mine.completedAt ? `· ${mine.completedAt.slice(0, 10)}` : ''}
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">Progress</span>
                <span className="font-bold text-slate-800">{mine.progress}%</span>
              </div>
              <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full rounded-full transition-all ${mine.status === 'completed' ? 'bg-emerald-500' : 'bg-gradient-to-r from-brand-500 to-brand-600'}`}
                  style={{ width: `${mine.progress}%` }}
                />
              </div>
              {mine.status !== 'completed' && (
                <div className="mt-4 space-y-3">
                  {course.content ? (
                    <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-2.5">
                          <Layers size={16} className="mt-0.5 shrink-0 text-brand-600" />
                          <div>
                            <p className="text-xs font-bold text-brand-900">Course content</p>
                            <p className="mt-0.5 text-[11px] text-brand-700">
                              {course.content.moduleCount} module{course.content.moduleCount === 1 ? '' : 's'} · {course.content.lessonCount} lessons
                              {course.content.totalMinutes > 0 && ` · ~${course.content.totalMinutes} min`} · progress tracks lesson completion
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/courses/${course.id}/learn`)}
                          className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-brand-500/25 transition-all hover:brightness-110"
                        >
                          {mine.progress > 0 ? 'Continue learning' : 'Start learning'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {progressSteps.filter((p) => p < 100 || !course.quiz).map((p) => (
                      <button
                        key={p}
                        onClick={() => setProgress(p)}
                        disabled={acting || p === mine.progress}
                        className={`rounded-xl px-3.5 py-2 text-xs font-semibold transition-all disabled:opacity-40 ${
                          p === mine.progress
                            ? 'bg-brand-600 text-white shadow-md shadow-brand-500/25'
                            : 'border border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700'
                        }`}
                      >
                        {p === 100 ? 'Mark complete' : `${p}%`}
                      </button>
                    ))}
                  </div>
                  )}
                  {course.quiz && (
                    <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-2.5">
                          <ClipboardList size={16} className="mt-0.5 shrink-0 text-brand-600" />
                          <div>
                            <p className="text-xs font-bold text-brand-900">{course.quiz.title}</p>
                            <p className="mt-0.5 text-[11px] text-brand-700">
                              {course.quiz.questionCount} questions · pass at {course.quiz.passScore}% to complete the course
                              {course.myQuizBest &&
                                ` · best ${course.myQuizBest.scorePercent}% over ${course.myQuizBest.attemptCount} attempt${course.myQuizBest.attemptCount === 1 ? '' : 's'}`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/courses/${course.id}/quiz`)}
                          className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-brand-500/25 transition-all hover:brightness-110"
                        >
                          {course.myQuizBest ? 'Retake quiz' : 'Take quiz'}
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={drop}
                    disabled={acting}
                    className="text-xs font-semibold text-slate-400 underline-offset-2 hover:text-red-500 hover:underline disabled:opacity-40"
                  >
                    Drop this course
                  </button>
                </div>
              )}
              {mine.status === 'completed' && course.certValidityMonths && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600">
                  <Award size={13} /> A certification was issued — see the Certifications tab.
                </p>
              )}
              {mine.status === 'completed' && course.content && (
                <button
                  onClick={() => navigate(`/courses/${course.id}/learn`)}
                  className="mt-3 rounded-xl border border-brand-200 bg-brand-50 px-3.5 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                >
                  Review lessons
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50/40 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
                  <BookOpen size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold">
                    {canEnroll ? 'Ready to start learning?' : course.status !== 'published' ? 'This course is not open for enrollment' : 'Enrollment unavailable'}
                  </p>
                  <p className="text-xs text-slate-400">
                    {full ? 'This course is full — check back later.' : canEnroll ? 'Enroll to track your progress and earn a certificate.' : ''}
                  </p>
                </div>
              </div>
              {canEnroll && (
                <button
                  onClick={enroll}
                  disabled={acting || full}
                  className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {acting ? 'Enrolling…' : full ? 'Course full' : 'Enroll now'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
